import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { ModalShell } from '@/components/ui/ModalShell'
import { useModalStore } from '@/hooks/useModalStore'
import { formatShortcut, getPrimaryModifierLabel } from '@/utils/keyboardShortcuts'

interface ShortcutEntry {
  keys: string[]
  description: string
}

interface ShortcutSection {
  title: string
  entries: ShortcutEntry[]
}

function KeyBadge({ label }: { label: string }) {
  return (
    <kbd
      className="inline-flex items-center justify-center rounded border px-1.5 py-0.5 text-[10px] font-medium font-mono leading-none"
      style={{
        background: 'var(--bg-input)',
        borderColor: 'var(--border)',
        color: 'var(--text)',
        boxShadow: '0 1px 0 var(--border)',
        minWidth: 24,
      }}
    >
      {label}
    </kbd>
  )
}

function ShortcutRow({ keys, description, orLabel }: ShortcutEntry & { orLabel: string }) {
  return (
    <div
      className="flex items-center justify-between gap-4 px-4 py-2.5 text-[11px] border-b last:border-b-0"
      style={{ borderColor: 'var(--border)' }}
    >
      <span style={{ color: 'var(--text-muted)' }}>{description}</span>
      <div className="flex shrink-0 items-center gap-1">
        {keys.map((key, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <span className="text-[9px]" style={{ color: 'var(--text-dim)' }}>{orLabel}</span>}
            <div className="flex items-center gap-0.5">
              {key.split('+').map((part, j) => (
                <span key={j} className="flex items-center gap-0.5">
                  {j > 0 && <span className="text-[9px]" style={{ color: 'var(--text-dim)' }}>+</span>}
                  <KeyBadge label={part} />
                </span>
              ))}
            </div>
          </span>
        ))}
      </div>
    </div>
  )
}

export function ShortcutsModal() {
  const { t } = useTranslation()
  const open = useModalStore((state) => state.shortcutsOpen)
  const close = useModalStore((state) => state.closeShortcuts)
  const modifierLabel = useMemo(getPrimaryModifierLabel, [])

  const sections = useMemo<ShortcutSection[]>(
    () => [
      {
        title: t('shortcuts.file'),
        entries: [
          { keys: formatShortcut(['Ctrl+I'], modifierLabel), description: t('shortcuts.importProject') },
          { keys: formatShortcut(['Ctrl+E'], modifierLabel), description: t('shortcuts.exportProject') },
        ],
      },
      {
        title: t('shortcuts.edit'),
        entries: [
          { keys: formatShortcut(['Ctrl+Z'], modifierLabel), description: t('shortcuts.undo') },
          { keys: formatShortcut(['Ctrl+Y', 'Ctrl+Shift+Z'], modifierLabel), description: t('shortcuts.redo') },
          { keys: formatShortcut(['Ctrl+A'], modifierLabel), description: t('shortcuts.selectAllTables') },
          { keys: formatShortcut(['Ctrl+C'], modifierLabel), description: t('shortcuts.copyTable') },
          { keys: formatShortcut(['Ctrl+V'], modifierLabel), description: t('shortcuts.pasteTable') },
          { keys: formatShortcut(['Ctrl+D'], modifierLabel), description: t('shortcuts.duplicateTable') },
          { keys: ['Delete', 'Backspace'], description: t('shortcuts.deleteSelection') },
        ],
      },
      {
        title: t('shortcuts.canvas'),
        entries: [
          { keys: ['T'], description: t('shortcuts.createTableAtMouse') },
          { keys: ['R'], description: t('shortcuts.createRelationship') },
          { keys: ['M'], description: t('shortcuts.moveMode') },
          { keys: ['S'], description: t('shortcuts.selectMode') },
        ],
      },
      {
        title: t('shortcuts.general'),
        entries: [
          { keys: ['Escape'], description: t('shortcuts.escape') },
        ],
      },
    ],
    [modifierLabel, t],
  )

  return (
    <ModalShell
      open={open}
      title={t('shortcuts.title')}
      description={t('shortcuts.description')}
      widthClassName="max-w-xl"
      onClose={close}
      footer={(
        <Button variant="primary" onClick={close}>
          {t('common.close')}
        </Button>
      )}
    >
      <div className="space-y-4">
        {sections.map((section) => (
          <div key={section.title}>
            <div
              className="mb-1 px-4 text-[9px] font-semibold uppercase tracking-widest"
              style={{ color: 'var(--text-dim)' }}
            >
              {section.title}
            </div>
            <div
              className="overflow-hidden rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              {section.entries.map((entry) => (
                <ShortcutRow key={entry.keys.join('|')} {...entry} orLabel={t('common.or')} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </ModalShell>
  )
}
